'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { LocaleForm, type LocaleFormRef } from '@/components/partials/locales'
import { FormSkeleton, ContentError } from '@/components/partials'
import { useUpdateLocale, useLocale } from '@/hooks'
import { useMetaStore } from '@/store'
import { UpdateLocaleParams } from '@/types/locale'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const UpdateLocale = ({ id }: PropsType) => {
  const router = useRouter()
  const t = useTranslations()
  const queryClient = useQueryClient()
  const [isRedirecting, setIsRedirecting] = useState(false)
  const localeFormRef = useRef<LocaleFormRef>(null)
  const getMeta = useMetaStore(state => state.getMeta)

  const { data: localeData, isLoading, error } = useLocale(id)
  const { mutate: updateLocale, isPending } = useUpdateLocale()

  const handleSubmit = (data: UpdateLocaleParams) => {
    updateLocale(
      { localeId: id, params: data },
      {
        onSuccess: async updatedLocale => {
          setIsRedirecting(true)
          queryClient.setQueryData(['locale', id], updatedLocale)
          await queryClient.invalidateQueries({ queryKey: ['locales'] })
          await getMeta()
          toast.success(t('updated_successfully'))
          router.push(`/localization/locales/${id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (localeFormRef.current) {
        localeFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/localization/locales/${id}`)
  }

  if (isLoading) {
    return <FormSkeleton fieldRows={2} showTranslations={false} />
  }

  if (error || !localeData) {
    return <ContentError error={error} listUrl="/localization/locales" />
  }

  return (
    <LocaleForm
      ref={localeFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={localeData}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default UpdateLocale
